// Pure, dependency-free geometry helpers for OUTER LABEL-PILL collision
// resolution on the wheel renderer (createPie.js `updateLable`).
//
// Why this exists: every outer label pill is anchored at the same radius
// (~1.2 * pieOutterRadius) at its segment's mid-angle. On a many-segment wheel
// rendered small (e.g. an 18-segment wheel at 375px mobile), adjacent pills
// pack tightly and overlap/occlude one another (Wave-5 QA: "Segment 17" hidden
// behind 16/18 at 12 o'clock). createPie measures each rendered pill's box and
// asks this module which pills can stay, degrading the rest full -> dot (the
// adaptive-ladder law from the deck's adaptive-icons-elevated §07, applied to
// labels — but keyed on MEASURED overlap because a pill's footprint depends on
// its text width, not the wheel diameter alone). ISP-9999 (Gordon 2026-08-31):
// the ladder used to have a third "hidden" rung when even a dot had no clear
// room; that let a segment's pill disappear entirely (wheel 2642), so it was
// removed — every segment always keeps at least a dot, rendered OVERLAPPED if
// necessary, never hidden. See resolveLabelOverlaps (createPie.js) /
// resolvePillOverlaps (renderer-v2/engine.js) for the paint-order rule this
// implies (earlier clockwise segment on top).
//
// Everything here is pure rectangle math so it is unit-testable without d3 or a
// DOM. A "rect" is any object with numeric { left, top, right, bottom } (a
// browser DOMRect satisfies this).

// ── Full-circle (single-segment) wedge detection (SINGLE SOURCE OF TRUTH) ───
// fix-daniyal-should-come-center-when-segment-only-one-web: a wheel's only
// segment spans the full circle (startAngle 0, endAngle 2π). Every rim-pill
// anchor below is keyed on the wedge's MID-angle, but a full-circle wedge has
// no real direction — its midAngle is still some arbitrary point (π, straight
// down for a D3/v2 pie layout), which rim-hugged the lone pill to the bottom
// of an otherwise featureless circle instead of the wheel's center. MEASURED
// live on web-stg.iyuga.net (wheel 507143, bundle main.6d5c0152.js): a
// 1-segment wheel's pill rendered at `translate(~0, outerRadius·1.05+rimGap)`,
// stranded below the ring. Both engines import this same check so a wedge
// counts as "full circle" identically by construction:
//   · D3Wheels/createPie.js   (v1 engine — updateLable pill transform)
//   · renderer-v2/geometry.js (v2 engine — pillLabelAnchor / labelAnchor)
export const FULL_CIRCLE_EPSILON = 1e-6;

export function isFullCircleWedge(startAngle, endAngle) {
  return endAngle - startAngle >= Math.PI * 2 - FULL_CIRCLE_EPSILON;
}

// ── Overlap-degradation ladder tuning (SINGLE SOURCE OF TRUTH) ───────────────
// The two constants that key the full-pill -> dot -> hidden ladder. They live
// HERE (not in a renderer) so BOTH wheel engines import the identical values and
// their ladders engage at the same density by construction:
//   · D3Wheels/createPie.js  (v1 engine — resolveLabelOverlaps / demoteLabelToDot)
//   · renderer-v2/engine.js  (v2 engine — resolvePillOverlaps / demotePillToDot)
// Moving them here is behaviour-neutral (the values are unchanged from v1's
// original createPie.js definitions) and removes the drift risk of two copies.

// Two pills that sit within this many px of each other count as colliding.
// ISP-9999 (Gordon 2026-08-12): the rim-pill ladder must PRIORITIZE each pill
// staying ANCHORED to its own segment over inter-pill spacing. A positive gap
// demanded breathing room and made the resolver eager to move/degrade a pill
// that was merely near a neighbour — which fed the outward drift (below). We
// reduce the minimum gap to 0 so adjacent pills may sit right up against each
// other (touching) before they even count as colliding — "reduce the minimum
// margin/gap between adjacent title pills." Both engines import this, so the
// density at which the ladder engages stays identical across engines.
export const LABEL_PILL_GAP_PX = 0;

// Radius (px) of the small colored "dot" a pill degrades to when it has no room
// for the full pill but the wheel is not yet too dense to show a marker.
export const LABEL_DOT_RADIUS_PX = 4;

// ── Label text truncation (SINGLE SOURCE OF TRUTH) ───────────────────────────
// Max characters shown in a rendered Segment-title label before it is truncated
// with a trailing ellipsis. Ported verbatim from the v1 D3 engine
// (createPie.js: `this.segmentLableLimit = config?.segmentLableLimit || 25`),
// which caps every rim-pill / label text at 25 chars + "..." so a long title
// can never run past the viewport edge on a space-constrained (dense / narrow)
// wheel. It lives HERE — beside the shared ladder constants — so BOTH wheel
// engines import the identical limit and truncate at the same length by
// construction (renderer-v2/engine.js was missing this, so long v2 labels
// overflowed the viewport; RENDERER-V2-REAL-DIFF R1).
export const SEGMENT_LABEL_CHAR_LIMIT = 25;

// Truncate a label to `limit` chars + "..." — v1 parity, exactly
// `title.length > limit ? title.slice(0, limit) + "..." : title`. Pure (no DOM)
// so it is unit-testable in isolation. Nullish / non-string input returns "".
export function truncateLabel(title, limit = SEGMENT_LABEL_CHAR_LIMIT) {
  const s = title == null ? "" : String(title);
  return s.length > limit ? s.slice(0, limit) + "..." : s;
}

// GVW-P02: middle-ellipsis truncation. Same total visible-character budget as
// truncateLabel (limit + "..."), so it doesn't reopen the 390px off-screen bug
// PHONE_LABEL_LIMIT was set to fix — it just spends that budget on a head AND
// a tail instead of an all-head cap, so same-prefix names ("Segment Alpha" /
// "Segment Beta") stay distinguishable instead of both collapsing to
// "Segment...".
export function truncateLabelMiddle(title, limit = SEGMENT_LABEL_CHAR_LIMIT) {
  const s = title == null ? "" : String(title);
  if (s.length <= limit) return s;
  const head = Math.ceil(limit / 2);
  const tail = Math.floor(limit / 2);
  return s.slice(0, head) + "..." + s.slice(s.length - tail);
}

// T22.188 (Gordon decision 2026-10-13, pairs with T22.176): the phone View
// Wheel shows a long segment title on ONE line cut off with "..." again
// instead of wrapping it into a tall multi-line pill (MEASURED live web-stg
// main.5c953e00 /wheel/507029 @390: 7-char wrap -> 8-line ~150px pills).
// Grapheme-safe (an emoji/flag is never split in half) and trims the space a
// word-boundary cut would leave before the ellipsis ("Capital..." not
// "Capital ..."). Always returns exactly one line; a title within the limit
// is returned untouched.
export function truncateLabelEnd(title, limit = SEGMENT_LABEL_CHAR_LIMIT) {
  const s = title == null ? "" : String(title);
  const units = graphemeUnits(s);
  const max = Math.max(1, Math.floor(limit) || 1);
  if (units.length <= max) return s;
  return units.slice(0, max).join("").replace(/\s+$/, "") + "...";
}

// ── Label WRAPPING (SINGLE SOURCE OF TRUTH — both engines) ──────────────────
// gv-0914c (Gordon video #3, "always full titles"): an all-head ellipsis
// truncation clips a title no matter how much room the surface actually has —
// no window width can ever satisfy "show the whole title" through truncation
// alone. Word-wrap it across as many lines as it takes instead: each line
// stays within the same per-position budget the caller's char-limit already
// computed (so a line can never clip past its pane edge, the same guarantee
// a truncation gave), but the text is NEVER CUT — no ellipsis, ever, at any
// length (createPie.segmentTitleNeverEllipsis.test.js proves this for
// titles up to 68 chars). Deliberately UNBOUNDED in line count — see that
// test's own "wraps onto more lines on the narrow canvas" assertion for why
// a hard line cap is out of scope here: it would reintroduce ellipsis
// truncation for long titles, the exact regression this function exists to
// prevent. (fix-vy0918d-rimpill-ellipsis-truncates-view-page PR body has a
// WORDING/DESIGN-FOR-GORDON note on this — the bug's own dispatch note
// paraphrased "FE #5546's intent" as "no unbounded wrapping past two lines",
// which does not match what #5546 actually shipped/tests.)
// Originally a private, duplicated pair of helpers inside v1's createPie.js —
// moved here (fix-vy0918d-rimpill-ellipsis-truncates-view-page) so BOTH wheel
// engines (v1 createPie.js, v2 renderer-v2/engine.js) wrap identically instead
// of only v1 wrapping while v2 still hard-truncated with "...".
export function wrapLabelIntoLines(str, maxUnitsPerLine) {
  const s = str == null ? "" : String(str);
  const limit = Math.max(1, Math.floor(maxUnitsPerLine) || 1);
  let units = graphemeUnits(s);
  if (units.length <= limit) return [s];
  const lines = [];
  while (units.length > limit) {
    // Prefer breaking at the last space within budget so words stay whole;
    // fall back to a hard break at the budget so a single overlong word
    // still makes progress (never drops text — no ellipsis, ever).
    let breakAt = limit;
    for (let i = Math.min(limit, units.length - 1); i > 0; i--) {
      if (units[i] === " ") {
        breakAt = i;
        break;
      }
    }
    // fix-kelly-u09gjpu7ysx-usman-had-me-delete-native-app-mobile: a hard break
    // (no space found in budget) that leaves only a 1-2 unit tail before the
    // next space/end-of-string strands that tail as its own near-empty line —
    // MEASURED live on web-stg.iyuga.net (wheel 507140, 390px): "Shipping"
    // rendered as tspans "Shippin" / "g", "Exclusive" as "Exclusi" / "ve".
    // Folding that short tail into the current line (accepting a couple units
    // of overflow past the budget) reads as a clean wrap instead of a broken
    // orphan line.
    if (units[breakAt] !== " ") {
      let wordEnd = breakAt;
      while (wordEnd < units.length && units[wordEnd] !== " ") wordEnd++;
      if (wordEnd - breakAt <= 2) breakAt = wordEnd;
    }
    lines.push(units.slice(0, breakAt).join(""));
    units = units.slice(breakAt);
    if (units[0] === " ") units = units.slice(1);
  }
  if (units.length) lines.push(units.join(""));
  return lines;
}

// gv-0914c: renders a label's wrapped lines as stacked <tspan>s, replacing
// any prior render (upsert-safe for hover/selection re-layout). `x=0` on
// every tspan re-anchors each line to the text element's own text-anchor
// (start/middle/end) instead of inheriting the previous line's end position.
// Vertical placement keeps the existing single-line baseline convention
// (dy="0.35em" from the anchor) byte-identical when there's exactly one
// line, and centers the whole block on the anchor when there's more than one.
export const LABEL_LINE_HEIGHT_EM = 1.15;

export function renderLabelLines(textNode, lines) {
  while (textNode.firstChild) textNode.removeChild(textNode.firstChild);
  const n = lines.length;
  lines.forEach((line, i) => {
    const tspan = document.createElementNS("http://www.w3.org/2000/svg", "tspan");
    tspan.setAttribute("x", 0);
    tspan.setAttribute(
      "dy",
      i === 0
        ? `${0.35 - ((n - 1) * LABEL_LINE_HEIGHT_EM) / 2}em`
        : `${LABEL_LINE_HEIGHT_EM}em`
    );
    tspan.textContent = line;
    textNode.appendChild(tspan);
  });
}

// ── Middle-truncation COLLISION AVOIDANCE (SINGLE SOURCE OF TRUTH) ──────────
// ISP-9999 rs-0914-dev-segment-labels-truncate-10ch-390 (PR #5497): two
// DIFFERENT segment titles can share both a common prefix AND a common suffix
// within the head+tail budget and so collapse to the IDENTICAL truncated
// string — MEASURED live (web-dev.iyuga.net, wheel 6232, phone limit 7):
// "Segment 1" and "Segment 1Segment 1" both rendered "Segm...t 1". This fix
// originally landed ONLY inside the v1 D3 engine (createPie.js), as private
// module-scope helpers never exported here — so the v2 engine (this file's
// other caller, renderer-v2/engine.js) kept the bug (fix-fe-5497-rimpill-
// collision-viewwheel-regression-0917). Moved here so BOTH engines share the
// identical disambiguation policy by construction, same pattern as the
// overlap-ladder constants above.
//
// graphemeUnits: split by GRAPHEME (Intl.Segmenter) so an emoji/flag/ZWJ
// sequence counts as one visible unit; falls back to code-points
// (Array.from, still surrogate-pair safe) where Segmenter is unavailable.
export function graphemeUnits(str) {
  const s = str == null ? "" : String(str);
  try {
    if (typeof Intl !== "undefined" && typeof Intl.Segmenter === "function") {
      return Array.from(
        new Intl.Segmenter(undefined, { granularity: "grapheme" }).segment(s),
        (seg) => seg.segment
      );
    }
    return Array.from(s);
  } catch (e) {
    return Array.from(s);
  }
}

// Given a title whose middle-truncated candidate is already `taken` by an
// earlier, DIFFERENTLY-titled label, try the other (head, tail) splits that
// spend the SAME total visible-unit budget (so the pill can never widen past
// what its caller's limit already bounded) until one isn't in `taken`.
// Pure + grapheme-safe. Falls back to the original (default-split) candidate
// — still a collision — only when no split can tell the pair apart.
export function disambiguateMiddleTruncation(title, limit, taken) {
  const s = title == null ? "" : String(title);
  const units = graphemeUnits(s);
  if (units.length <= limit) return s;
  const defaultHead = Math.ceil(limit / 2);
  const defaultTail = Math.floor(limit / 2);
  const candidateFor = (head, tail) =>
    tail > 0
      ? units.slice(0, head).join("") + "..." + units.slice(units.length - tail).join("")
      : units.slice(0, head).join("") + "...";
  const splits = [[defaultHead, defaultTail]];
  for (let head = limit; head >= 0; head--) {
    if (head === defaultHead) continue;
    splits.push([head, limit - head]);
  }
  for (const [head, tail] of splits) {
    const candidate = candidateFor(head, tail);
    if (!taken.has(candidate)) return candidate;
  }
  return candidateFor(defaultHead, defaultTail);
}

// Do two axis-aligned rectangles overlap? An optional `gap` (px) inflates each
// box so pills that merely touch (or sit within `gap` of each other) count as
// colliding — labels need a little breathing room, not just non-intersection.
export function rectsOverlap(a, b, gap = 0) {
  if (!a || !b) return false;
  return (
    a.left < b.right + gap &&
    a.right > b.left - gap &&
    a.top < b.bottom + gap &&
    a.bottom > b.top - gap
  );
}

// Greedy placement: walk `candidates` (each { id, rect }) in the given priority
// order and keep a candidate only if its rect does not collide (within `gap`)
// with any already-kept rect. Returns { kept: Set<id>, rejected: Set<id> } plus
// the list of placed rects so a caller can run a second pass (e.g. dots) against
// the same accumulator. Deterministic: earlier candidates win ties.
export function greedyPlace(candidates, gap = 0, seedRects = []) {
  const placed = seedRects.slice();
  const kept = new Set();
  const rejected = new Set();
  for (const c of candidates || []) {
    if (!c || !c.rect) {
      rejected.add(c && c.id);
      continue;
    }
    const collides = placed.some((p) => rectsOverlap(c.rect, p, gap));
    if (collides) {
      rejected.add(c.id);
    } else {
      placed.push(c.rect);
      kept.add(c.id);
    }
  }
  return { kept, rejected, placed };
}

// A rect is "measurable" when it has real, non-zero size. A display:none /
// unrendered node yields a zero box (and must be ignored, not treated as a
// point collider).
export function isMeasurableRect(rect) {
  return (
    !!rect &&
    typeof rect.width === "number" &&
    typeof rect.height === "number" &&
    rect.width > 0 &&
    rect.height > 0
  );
}

// ── Keep-anchored overlap tolerance (SINGLE SOURCE OF TRUTH — both engines) ──
// ISP-9999 (Gordon 2026-08-12): "prioritize each pill staying ANCHORED/aligned
// to its own segment over inter-pill spacing … even allow a little OVERLAP
// rather than let a pill drift away from its segment. The collision resolver
// should give up spacing before it gives up alignment." So when a full pill
// collides at its rim anchor we now PREFER leaving it exactly where it is (at
// its segment's angular centre) and accepting a bounded overlap, instead of
// nudging it radially outward (which sacrificed alignment — the reported bug:
// the bottom-cluster pills drifted 33–80px off the rim). We only stop keeping a
// pill "full" once a neighbour would occlude more than this fraction of its box,
// at which point it degrades to a compact dot AT ITS ANCHOR — never a drift.
// 0.45 ⇒ up to ~45% of a pill may be covered and it still reads (the leading
// text edge stays visible); past that it is demoted so labels never fully
// occlude one another.
//
// ISP-9999 (Gordon 2026-08-28): in production a bounded overlap still let two
// truncated pills render text-on-text (QA-measured on wheel 2642: "Customer
// Success & Suppor..." / "QAsave557763" overlapped 108x11px) — 45% coverage of
// a SMALL neighbour's box can still be most of that neighbour, because the
// ratio is checked against the CANDIDATE pill's own area, not the pill it
// lands on top of. Both resolvers (createPie.js resolveLabelOverlaps,
// renderer-v2/engine.js resolvePillOverlaps) now call `canKeepAnchored(rect,
// placed, 0)` — an explicit 0 override, not this default — so a full/dot pill
// keeps its anchor only when it has ZERO overlap with every already-placed
// rect; any actual overlap degrades it (still AT its own anchor, never a
// drift). This constant stays the exported default for canKeepAnchored's
// generic (non-zero-tolerance) use, and for its own unit tests below.
export const LABEL_KEEP_MAX_OVERLAP_RATIO = 0.45;

// Area of the intersection of two axis-aligned rects (0 when disjoint). Pure.
export function intersectionArea(a, b) {
  if (!a || !b) return 0;
  const w = Math.min(a.right, b.right) - Math.max(a.left, b.left);
  const h = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top);
  return w > 0 && h > 0 ? w * h : 0;
}

// Fraction of `target`'s own area covered by the MOST-overlapping rect in
// `others` (0 = clear, 1 = fully covered). This is the readability metric the
// keep-anchored decision keys on: how much of THIS pill a neighbour hides.
export function coveredRatio(target, others) {
  if (!target) return 0;
  const area =
    (target.width != null ? target.width : target.right - target.left) *
    (target.height != null ? target.height : target.bottom - target.top);
  if (!(area > 0)) return 0;
  let maxCov = 0;
  for (const o of others || []) {
    const cov = intersectionArea(target, o) / area;
    if (cov > maxCov) maxCov = cov;
  }
  return maxCov;
}

// May a colliding full pill STAY at its segment anchor (accepting the overlap)
// instead of being moved/degraded? Yes while no neighbour covers more than
// `maxRatio` of it — alignment is preferred over spacing up to that readability
// bound. Pure + shared so both engines make the identical call.
export function canKeepAnchored(
  full,
  placed,
  maxRatio = LABEL_KEEP_MAX_OVERLAP_RATIO
) {
  return coveredRatio(full, placed) <= maxRatio;
}

// ── Label-vs-WHEEL collision (SINGLE SOURCE OF TRUTH — both engines) ────────
// gordon-0920-view-wheel-segment-title-labels-must-never-overlap-the-wheel:
// owner report + design law (rim-pill-never-hide-clockwise-overlap decision
// log) — a segment title pill MAY overlap ANOTHER pill (see the
// keep-anchored law above) but must NEVER cover any pixel of the wheel
// itself (outer rim / any wedge / the portrait). MEASURED on wheel 4908
// (1920x1080, web-dev): several multi-line pills (e.g. "Exploring Modern and
// Contemporary Furniture Designs") painted their rect OVER the outer ring.
//
// ROOT CAUSE (both engines, byte-identical bug): the pill's anchor point was
// placed at a FIXED radius (`outerRadius*1.05 + rimGap`) along the segment's
// bisector — a constant that only reflects the GAP, never the pill's own
// rendered size. A pill's background <rect> is centered on that anchor
// vertically (renderLabelLines' dy centering) and only offset horizontally by
// its text-anchor, so a tall (multi-line) or wide pill's near edge/corner can
// sit CLOSER to the wheel's centre than the anchor itself — inside the fixed
// gap, sometimes inside the ring.
//
// FIX: a pill's anchor must be pushed out by the pill's own SUPPORT distance
// (the standard "furthest extent of an axis-aligned box, in a given
// direction, from its own centre") in the radial direction, not just the
// flat gap. For a box with half-width `halfWidth` and half-height
// `halfHeight`, centred at a point `radius` out along direction
// (sin(midAngle), -cos(midAngle)) [this module's/geometry.js's polar
// convention: 0 = 12 o'clock, clockwise], the box's support in that exact
// direction is `halfWidth*|sin(midAngle)| + halfHeight*|cos(midAngle)|`.
// Placing the box centre at `minRadius + support` guarantees EVERY point in
// the box is at distance >= minRadius from the origin (proof: the box lies
// entirely in the half-plane {P : P·d >= centre·d - support}, and
// |P| >= P·d since d is a unit vector) — this holds for ANY angle and ANY
// box size, which is what pillSafeAnchorRadius.geometry.test.js asserts
// exhaustively (the prove-can-fail law: a pill placed 10px short of this
// radius must measure inside minRadius, i.e. FAIL).
//
// `minRadius` is the caller's `outerRadius + gap` (the popped/selected outer
// radius when the wedge is popped out — callers must pass the RENDERED
// radius, not the resting one, so a selected wedge's grown rim still gets
// its labels pushed out to match).
export function pillSafeAnchorRadius(minRadius, midAngle, halfWidth, halfHeight) {
  const dx = Math.abs(Math.sin(midAngle));
  const dy = Math.abs(Math.cos(midAngle));
  const support = Math.max(0, halfWidth) * dx + Math.max(0, halfHeight) * dy;
  return Math.max(0, minRadius) + support;
}

// Guaranteed LOWER BOUND on a box's distance from the origin, given the box
// is centred at `anchorRadius` out along `midAngle` with the half-extents
// above. Exists purely so both the fix and its regression test can compute
// the same "worst case nearest point" number — used by
// pillSafeAnchorRadius.geometry.test.js's positive control (moving a label
// 10px inward must measure BELOW the design gap).
export function pillWorstCaseDistance(anchorRadius, midAngle, halfWidth, halfHeight) {
  const dx = Math.abs(Math.sin(midAngle));
  const dy = Math.abs(Math.cos(midAngle));
  const support = Math.max(0, halfWidth) * dx + Math.max(0, halfHeight) * dy;
  return anchorRadius - support;
}

// ── Font-ready re-place (gordon-0920-labels-firefox) ─────────────────────────
// pillSafeAnchorRadius's guarantee is only as good as the halfWidth/halfHeight
// it is fed, and BOTH engines source those from a `text` node's LIVE
// `getBBox()` (createPie.js loadRectSize / renderer-v2/engine.js renderPill),
// taken synchronously on first draw. If the DM Sans web font has not finished
// loading at that instant, the browser measures the fallback font's box
// instead — a different width/height per engine's fallback-font metrics — and
// once DM Sans swaps in, the PAINTED text no longer matches the box the
// push-out radius was computed from. Measured live on web-dev.iyuga.net
// (main.3795c4fd.js, Firefox, 1440w, /wheel/4908): 7 of 8 pills landed 3-7.3px
// INSIDE the required 8px rim gap — Chromium/WebKit happened to already have
// DM Sans warm (cached from an earlier same-origin load in this harness), so
// only Firefox's cold-cache timing exposed it. This is a timing race, not a
// per-browser metric difference, so the fix is one shared re-place hook, not
// a per-engine fork.
//
// `doc.fonts.ready` resolves once the fonts needed for the CURRENT layout are
// loaded, but a font that was not yet requested at first paint (e.g. this
// component mounting before anything else on the page needed DM Sans) can
// still finish loading AFTER `ready` already resolved once; `loadingdone`
// fires for that case too, so both are wired — `relayout` is idempotent
// (it's just "measure + place again"), so an extra call is harmless.
// Returns an unsubscribe function; always safe to call (no-op on a doc with no
// FontFaceSet, e.g. jsdom).
export function scheduleFontReadyRelayout(doc, relayout) {
  const fonts = doc && doc.fonts;
  if (!fonts || typeof relayout !== "function") return () => {};
  let destroyed = false;
  const run = () => {
    if (!destroyed) relayout();
  };
  if (typeof fonts.ready === "object" && typeof fonts.ready.then === "function") {
    fonts.ready.then(run, () => {});
  }
  const hasListener = typeof fonts.addEventListener === "function";
  if (hasListener) fonts.addEventListener("loadingdone", run);
  return () => {
    destroyed = true;
    if (hasListener) fonts.removeEventListener("loadingdone", run);
  };
}

// ── Settle re-verification (gordon-0928-P0-rim-labels-wrong-on-first-load-
// until-hover) ────────────────────────────────────────────────────────────
// MEASURED LIVE (web-dev.iyuga.net, /builder/4908, headless Chromium, cold
// load, ZERO pointer events): every upstream trigger above (a resize tick,
// the font-ready hook) runs its OWN single relayout pass and never checks its
// own result again. A MutationObserver on every rim-pill's `transform`
// attribute, watched across the whole cold-load sequence, caught the actual
// failure: 6 of 8 pills converged to a stable, ring-safe transform by ~5.3s
// and never moved again; the remaining 2 ("RegressionTitle-89177" and
// "Exploring Modern and Contemporary Furniture Designs" — matching the
// reporter's own two overlapping pills exactly) kept moving through several
// ticks and then, on their OWN final relayout pass, landed on a transform
// 45-52px SHORT of where their two PRIOR passes had already safely placed
// them — a regression, not a not-yet-converged state — and nothing ever
// re-measured them again: the deficit (measured -45.3px vs the required
// +8px gap) was still there, unchanged, 14+ seconds later. Two mouse moves
// across the wheel afterward (a real hover) changed nothing either — moving
// the mouse is not what actually fixes this on a live page; further wall-
// clock settling is.
//
// Root-causing the EXACT one-tick regression (which upstream measurement —
// pane width, font metrics, or something else — went stale on that specific
// pass) needs deeper live instrumentation than a black-box measurement can
// prove. But the FIX doesn't need the exact cause: `relayoutLabelPills()` is
// idempotent and self-correcting BY CONSTRUCTION (loadRectSize's own ring-
// safety loop re-measures the real, current DOM box every time it runs) —
// the only thing missing is a LATER call once the geometry has genuinely
// stopped changing. These are bounded, cheap (idempotent no-op when already
// correct) extra passes scheduled after every trigger that can legitimately
// change label geometry (a resize settle, a font-ready signal), so a single
// bad pass is never the last word.
export const RELAYOUT_SETTLE_DELAYS_MS = [300, 1200];

// ── FINAL RING-SAFETY CORRECTION (SINGLE SOURCE OF TRUTH — both engines) ────
// gordon-0920-EDIT-WHEEL-builder-labels-ROUND-2 (an independent breaker unit's
// repeat measurement AFTER FE #5899/#5895 both merged and deployed): every
// upstream placement step (pillSafeAnchorRadius's push-out above, the
// Builder's own pane-width clamp in labelRadialX.js) reasons in SVG-LOCAL
// units from an ESTIMATED box (loadRectSize / renderPill's own padded
// getBBox() rect), and assumes nothing else moves the box afterward and that
// local units map 1:1 onto on-screen page pixels. Two ways that broke, both
// measured live on the POST-FIX bundle (main.558f6ebc.js):
//   (1) Builder's clampAnchorTxToPane / clampPillBoxToPane can nudge a pill's
//       box BACK toward the pane centre after the ring-safety push-out
//       already ran, silently undoing it — measured: /builder LEFT-half
//       labels landing 20-43px INSIDE the required 8px rim gap, with
//       IDENTICAL numbers run after run (not a timing race — deterministic
//       geometry: the clamp fires every render, every time).
//   (2) A wheel engine's own text metrics for the SAME font differ a few px
//       per browser (Firefox vs Chromium getBBox()), so the push-out's own
//       halfWidth/halfHeight input is already slightly wrong on some engines
//       even before any clamp runs — measured: /wheel (View Wheel, no pane
//       clamp at all) Firefox labels landing 3.2-3.4px short of the required
//       8px at both 1440 and 1024, Chromium clean (8.1px) on the identical
//       wheel/titles.
// Rather than chase each upstream cause separately (and re-break the same
// invariant the next time a caller adds one more transform), this runs LAST,
// after every other placement step, and re-measures the box's REAL on-screen
// position via getBoundingClientRect() — the exact API + coordinate space the
// owner's own acceptance ruler uses — so it is correct regardless of which
// upstream step (or a future one) under- or over-shot. Both engines call this
// with their OWN live-rendered rects; never a synthetic/estimated box.

// Union bounding box (page-pixel space) of a set of already-rendered DOM
// elements, reduced to a {center, radius} pair — same "wedges union to the
// full circle's own bounding square" identity the owner's ruler uses, so a
// caller never needs a separate "find the ring" selector. Returns null when
// `elements` is empty/unmeasurable (nothing to correct against).
export function unionBBoxCenterRadius(elements) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const el of elements || []) {
    const r = el && typeof el.getBoundingClientRect === "function" ? el.getBoundingClientRect() : null;
    if (!r || !(r.width > 0 || r.height > 0)) continue;
    minX = Math.min(minX, r.left);
    minY = Math.min(minY, r.top);
    maxX = Math.max(maxX, r.right);
    maxY = Math.max(maxY, r.bottom);
  }
  if (!(maxX > minX) || !(maxY > minY)) return null;
  return {
    center: { x: (minX + maxX) / 2, y: (minY + maxY) / 2 },
    // average of half-width/half-height absorbs sub-pixel rounding rather
    // than picking one axis arbitrarily (same choice the owner's ruler makes).
    radius: ((maxX - minX) / 2 + (maxY - minY) / 2) / 2,
  };
}

// Bound on how many times a caller re-measures + re-corrects a single pill
// (see createPie.js loadRectSize / syncLabelSelectionTransforms and
// renderer-v2/engine.js renderPill). One pass covers the overwhelming
// majority of real cases; a bounded few more handle the rarer case where an
// upstream clamp pulled the box far enough to straddle the wheel's own
// centre, which can change which edge is nearest partway through a single
// push (see ringSafetyCorrection's own test for a worked example). Never
// unbounded — a caller must not be able to loop forever on a pathological
// input (e.g. a box wider than the wheel itself, which no finite push along
// a fixed bisector can ever fully clear).
export const RING_SAFETY_MAX_CORRECTION_PASSES = 6;

// How much FARTHER (>= 0) a real, already-rendered pill rect must move along
// its own outward radial direction so its nearest point clears the ring by
// `requiredGapPx`. Pure geometry (nearest-point-on-axis-aligned-rect-to-a-
// point, same formula the ruler uses) — 0 when the rect already clears.
export function ringSafetyCorrection(pillRect, wedgeCenter, wedgeRadius, requiredGapPx) {
  if (!pillRect || !wedgeCenter || !(wedgeRadius >= 0) || !(requiredGapPx >= 0)) {
    return 0;
  }
  const nearestX = Math.max(pillRect.left, Math.min(wedgeCenter.x, pillRect.right));
  const nearestY = Math.max(pillRect.top, Math.min(wedgeCenter.y, pillRect.bottom));
  const dist = Math.hypot(nearestX - wedgeCenter.x, nearestY - wedgeCenter.y);
  const gap = dist - wedgeRadius;
  const deficit = requiredGapPx - gap;
  return deficit > 0 ? deficit : 0;
}

// batch-proof-fe-dev-ab2970fb (Gordon 2026-10-02): resolveRimPillRadiusBumps
// (geometry.js) only ever pushes a crowded pill's radius OUTWARD to clear a
// pill-pill overlap — it has no notion of the actual browser viewport, so a
// pill already near the 3/9 o'clock meridian (where radius growth moves
// almost entirely horizontally) can be escalated straight past the screen
// edge. MEASURED live on web-dev.iyuga.net (bundle main.ab2970fb.js, the
// FE#6377 fix already live, 390x844 chromium+webkit, a 12-segment reporter-
// shaped wheel reusing FE#6377's own "Exclusive Coupon"/"Spin Again" pair):
// zero pill-pill overlap (the fix works) but 3 of 12 pills landed OFF the
// viewport with no way to scroll to them (document.documentElement.scrollWidth
// === clientWidth === 390, no horizontal overflow exists) — "Exclusive
// Coupon" itself (the exact pill FE#6377's escalation targets) 13px past the
// right edge, "Free Consultation" 36px past the left edge (~52% of the pill
// invisible). The July 2026 "RIM-PILL CLIP FIX" (engine.js render(),
// svg.style.overflow = "visible") deliberately stopped the SVG's own box from
// clipping a pill into the surrounding page whitespace — it says nothing
// about the pill also outrunning the ACTUAL device screen, which has no more
// whitespace past its own edge.
//
// Same shape as ringSafetyCorrection below, but bounding a MAXIMUM extent
// (the real viewport) instead of a minimum radius, and pulling the pill
// INWARD along its own anchor's fixed radial line rather than pushing it out
// — gordon-0915-law's "never hidden, fixed angle" is unchanged (the angle
// never moves, only how far out along it the pill sits), it's simply the
// SAME narrow "radius may be adjusted" exception the law already carves out
// for assignRimPillRadiusTiers/resolveRimPillRadiusBumps, now also bounded on
// the far side. A pill still may overlap a neighbour afterward if pulling it
// fully inside the viewport would mean crossing back under its ring-safety
// minimum — matching the existing "overlap is tolerated, never hiding" law —
// but that is strictly rarer than the viewport being narrower than a single
// pill's own rim gap, which never happens on a real device.
//
// `direction` is the SAME unit vector positionPill's own onCorrect callback
// already derives from the pill's midAngle (ux=sin(angle), uy=-cos(angle)):
// moving by scalar `c` along it shifts a REAL (already-rendered) rect by
// exactly (c*ux, c*uy), since the anchor translate is a pure translation.
export function viewportSafetyCorrection(pillRect, direction, viewportRect) {
  if (!pillRect || !direction || !viewportRect) return 0;
  const { ux, uy } = direction;

  let dxNeeded = 0;
  if (pillRect.right > viewportRect.right) dxNeeded = viewportRect.right - pillRect.right;
  else if (pillRect.left < viewportRect.left) dxNeeded = viewportRect.left - pillRect.left;

  let dyNeeded = 0;
  if (pillRect.bottom > viewportRect.bottom) dyNeeded = viewportRect.bottom - pillRect.bottom;
  else if (pillRect.top < viewportRect.top) dyNeeded = viewportRect.top - pillRect.top;

  if (dxNeeded === 0 && dyNeeded === 0) return 0;

  // Project each axis's required shift onto the fixed direction the pill is
  // actually free to move along; a single scalar correction can't satisfy
  // two independent axis targets at once unless the direction is axis-
  // aligned (the common case here — pills overflow horizontally near 3/9
  // o'clock, vertically near 6/12), so pick whichever axis needs the LARGER
  // magnitude correction — guarantees the worse violation actually clears.
  const candidates = [];
  if (Math.abs(ux) > 1e-6 && dxNeeded !== 0) candidates.push(dxNeeded / ux);
  if (Math.abs(uy) > 1e-6 && dyNeeded !== 0) candidates.push(dyNeeded / uy);
  if (!candidates.length) return 0;
  candidates.sort((a, b) => Math.abs(b) - Math.abs(a));
  return candidates[0];
}

// Same bounded-pass, suspend-the-transition-first shape as
// runRingSafetyCorrectionLoop (see its own header for why the transition
// must be suspended before any getBoundingClientRect() read is trustworthy),
// reused verbatim here so the viewport pull-back can't drift from the ring
// push-out's own measured behaviour. `onCorrect` receives a SIGNED
// correction (negative = pull inward) — callers already apply it with the
// same `anchor.x += correction*ux; anchor.y += correction*uy` math the ring
// loop uses, since viewportSafetyCorrection returns a value in that same
// convention.
export const VIEWPORT_SAFETY_MAX_CORRECTION_PASSES = 4;

export function runViewportSafetyCorrectionLoop({
  measureEl,
  transitionEl,
  direction,
  viewportRect,
  onCorrect,
}) {
  if (!measureEl || typeof measureEl.getBoundingClientRect !== "function") return 0;
  if (!onCorrect || !direction || !viewportRect) return 0;

  const hadTransitionEl = transitionEl && transitionEl.style;
  const prevTransition = hadTransitionEl ? transitionEl.style.transition : undefined;
  if (hadTransitionEl) {
    transitionEl.style.transition = "none";
    transitionEl.getBoundingClientRect();
  }
  let total = 0;
  try {
    for (let i = 0; i < VIEWPORT_SAFETY_MAX_CORRECTION_PASSES; i++) {
      const realRect = measureEl.getBoundingClientRect();
      const correction = viewportSafetyCorrection(realRect, direction, viewportRect);
      if (!correction) break;
      onCorrect(correction);
      total += correction;
    }
  } finally {
    if (hadTransitionEl) transitionEl.style.transition = prevTransition;
  }
  return total;
}

// dev-0921-P0 (ring-safety loop stacks 6x): every ringSafetyCorrection call
// site loops up to RING_SAFETY_MAX_CORRECTION_PASSES times, re-measuring the
// pill's REAL box via getBoundingClientRect() each pass and pushing it a
// little farther. Both engines also put a CSS `transition: transform
// <SEGMENT_PILL_GROW_DURATION_MS>ms ...` on that SAME pill <g> for the
// hover/select grow motion (engine.js renderPill sets it before this loop
// ever runs; createPie.js's updateLable sets it once per full draw and every
// later loadRectSize()/syncLabelSelectionTransforms() call reuses that same,
// already-transitioned node). A transitioned element does not commit a
// transform change to layout synchronously — reading
// getBoundingClientRect() right after mutating `transform` returns the
// PRE-correction box (measured live: setting a transform and re-reading it
// in the same synchronous block returns the OLD box; the read only reflects
// the new box once >=1 frame has elapsed). Every pass therefore sees the
// SAME stale box, computes the SAME deficit, and pushes again — after
// RING_SAFETY_MAX_CORRECTION_PASSES=6 passes the pill has been shoved
// 6x(wedgeRadius+requiredGapPx) instead of once (measured: View Wheel pills
// 1622px off a 261px wheel, i.e. off the viewport entirely = 6 x (261+8)).
//
// Fix: suspend the transition on the element that carries it for the
// duration of the loop (forces every mutation to commit instantly, so each
// getBoundingClientRect() read is the TRUE current box) and restore it
// afterward so hover/select grow motion is unaffected. One shared loop for
// both engines so neither can drift from the other's fix.
export function runRingSafetyCorrectionLoop({
  measureEl,
  transitionEl,
  wedgeCenter,
  wedgeRadius,
  requiredGapPx,
  onCorrect,
}) {
  if (!measureEl || typeof measureEl.getBoundingClientRect !== "function") return 0;
  if (!onCorrect || !wedgeCenter || !(wedgeRadius >= 0) || !(requiredGapPx >= 0)) return 0;

  const hadTransitionEl = transitionEl && transitionEl.style;
  const prevTransition = hadTransitionEl ? transitionEl.style.transition : undefined;
  if (hadTransitionEl) {
    transitionEl.style.transition = "none";
    // Force a synchronous style/layout flush so "none" is actually committed
    // before the first correction pass measures anything below.
    transitionEl.getBoundingClientRect();
  }
  let total = 0;
  try {
    for (let i = 0; i < RING_SAFETY_MAX_CORRECTION_PASSES; i++) {
      const realRect = measureEl.getBoundingClientRect();
      const correction = ringSafetyCorrection(realRect, wedgeCenter, wedgeRadius, requiredGapPx);
      if (!(correction > 0)) break;
      onCorrect(correction);
      total += correction;
    }
  } finally {
    if (hadTransitionEl) transitionEl.style.transition = prevTransition;
  }
  return total;
}

// ── Alternate-radius NUDGE — REMOVED (ISP-9999) ──────────────────────────────
// The engines used to rescue a colliding full pill by pushing it radially
// OUTWARD along its centroid ray (nudgeOutwardRect / LABEL_NUDGE_MAX_STEPS /
// LABEL_MAX_NUDGE_RESCUES). That traded ALIGNMENT for spacing — the exact
// behaviour Gordon rejected: on a tightly-packed wheel the bottom-cluster pills
// drifted 33–80px off the rim, ending up far from their own segments. The
// resolver now keeps a colliding pill at its segment anchor (canKeepAnchored,
// accepting a bounded overlap) and only degrades to a dot AT THE ANCHOR when a
// neighbour would occlude it — so a pill never leaves its segment. The outward-
// nudge helper and its two step/rescue caps were deleted with their last caller.
// ── PILL RULE A — tight, orderly vertical stack with pole extremum ─────────
// (SINGLE SOURCE OF TRUTH — both wheel engines: createPie.js resolveLabelOverlaps
// / renderer-v2/engine.js resolvePillOverlaps)
//
// stgfix-1009-T418 (Gordon, GORDON-DECISIONS.md, 2026-10-09 16:13+16:20 PT
// relaying Kelly, decided 17:00 PT as "Pill Rule A"): on a crowded wheel pill-pill
// collision now resolves differently from the canKeepAnchored law above —
// Pill Rule A explicitly WINS over the 2026-09-14 never-displaced law for THIS
// decision only:
//   (1) no pill-pill overlap when the rim has room to avoid it;
//   (2) when pills DO need to move to clear a collision, displaced neighbours
//       touch at exactly LABEL_PILL_GAP_PX (0px) — tight and orderly, never a
//       scattered/arbitrary gap (T4.18);
//   (3) on a crowded wheel the pill nearest 12 o'clock is the HIGHEST (smallest
//       y) of every top-half pill, and the pill nearest 6 o'clock is the LOWEST
//       (largest y) of every bottom-half pill — neighbours step away from the
//       pole, never above/below it (T4.19: the live bug had neighbours of the
//       12 o'clock pill sitting ABOVE it).
// The pill's ANGLE (which segment it is anchored to) never changes — only how
// far up/down it sits. This is a VERTICAL-only correction, layered after every
// other placement step (ring-safety, pane clamp, viewport pull-back) has
// already run and the pill is at its real, final, ring-/viewport-safe box.
//
// Rule (3)'s anchor is deliberately chosen by the pill's own SEGMENT ANGLE,
// never by its rendered box — T4.19's actual root cause is that a TALLER
// pill (a 2-line wrapped title) can get pushed to a slightly larger safe
// radius (pillSafeAnchorRadius) than a shorter neighbour, and near 12/6
// o'clock that extra radius is almost pure vertical travel — so the taller
// neighbour's rendered top can creep ABOVE the true 12 o'clock pill's even
// though it is angularly farther from noon. Keying "who is the pole pill"
// off rendered position would just inherit that same bug; keying it off
// angle fixes it by construction, independent of any text-height skew.
//
// Rule (3) never forces ordinary neighbours into a monotonic order: on a
// sparse wheel two pills can legitimately tie (or nearly tie) on angular
// pole-distance while their NATURAL radii correctly differ a lot (a long,
// multi-line title needs a far larger ring-safety push-out than a one-line
// neighbour), and clamping one against the other would override that
// necessary ring-safety placement. Only a real collision, or the pole
// pill itself (below), ever moves.
//
// stgfix-10101843-T419: the push direction is OUTWARD (away from the wheel:
// up in the 12 o'clock half, down in the 6 o'clock half), never inward.
// MEASURED live on web-stg.iyuga.net (main.a1a75c38.js, /builder/507662,
// /builder/507725, /builder/507726, 1440x900): the T4.18 version kept the
// pole pill fixed and pushed its NEIGHBOURS toward the pole's opposite side
// — which, for any pill near 12/6 o'clock, is straight back into the wheel.
// The ring-safety loop that runs right after (the unconditional "titles
// never overlap the wheel" law, which outranks Pill Rule A) then pushed
// every one of them straight back out, re-creating the overlap (Segment 32 x
// Segment 1 = 392px^2 even with the stack re-applied) and leaving
// neighbours level with / beyond the pole pill. Stacking outward can never
// fight ring-safety, so it is the only direction that can actually hold:
// pills are placed from the half's equator toward its pole, and each pill
// that collides with an already-placed (angularly farther) one is pushed
// outward just far enough to touch it at `gap`. The pole pill is therefore
// placed LAST and lands on top of the pyramid — rule (3) by construction.
//
// resolvePoleStack is the pure 1-D engine for ONE half (top or bottom). `pills`
// must already carry `poleDistance` (>= 0, smaller = angularly closer to this
// half's pole) and `awayFromPoleSign` (+1 = 12 o'clock half, -1 = 6 o'clock
// half; outward is the OPPOSITE sign) — see resolveTightPillStack below.
// `minTop`/`maxBottom` are the real room the pills have (the visible canvas):
// when the full stack would run past them, every displacement in the half is
// scaled down by the SAME factor so the stack just fits — the order (pole
// pill outermost, neighbours stepping away from it) is kept and the overlap
// that remains is spread evenly instead of piling the top two pills onto
// each other (Pill Rule A: "overlap only when there is truly no room").
//
// Rule (3) also holds when the pole pill does NOT collide with anything: its
// neighbours' boxes sit at a larger safe radius (pillSafeAnchorRadius's
// corner term), so near 12/6 o'clock they can naturally sit a pixel or so
// beyond the pole pill (MEASURED: /builder/507726, Segment 13 bottom 733.5 vs
// Segment 12/14 734.4). The pole pill is therefore clamped outward to at
// least the half's extreme among the OTHER pills. When two pills tie on angle
// (12/6 o'clock is a segment boundary) only the tie-break winner is clamped;
// the twin stacks like an ordinary pill (stgfix-10111425-T419). Only the pole
// pill is clamped — never an ordinary neighbour —
// so two angularly-tied, non-colliding pills elsewhere are never forced
// into a monotonic order (see the ring-safety side-pane regression test).
const POLE_TIE_EPSILON_RAD = 1e-9;

// stgfix-10120932-rimpills-symmetry (Gordon 2026-10-12, wheel 507342, 33
// segments, "CLEAN over exact angle"): when TWO pills compete for the pole
// spot — the pills nearest the pole on either side of the axis, at about the
// same angular distance (12/6 o'clock falls on or near the boundary between
// them) — they sit SIDE BY SIDE at the SAME height, split evenly left/right
// of the axis, instead of one stacked above the other (MEASURED web-stg
// main.36da05d4, /builder/508018 @1440: Segment 1 top 72 vs Segment 32 top
// 92). Needs `axisX` (the wheel's vertical axis in page px); without it the
// half stacks exactly as before. Each pair pill then carries `dx` as well.
const POLE_PAIR_MIN_RATIO = 1 / 3;

function sideOfAxis(p) {
  if (!Number.isFinite(p.midAngle)) return 0;
  const s = Math.sin(p.midAngle);
  return Math.abs(s) < 1e-9 ? 0 : Math.sign(s);
}

function findPolePair(valid) {
  if (valid.length < 2) return null;
  const nearest = (side) =>
    valid
      .filter((p) => sideOfAxis(p) === side)
      .reduce((best, p) => (!best || p.poleDistance < best.poleDistance ? p : best), null);
  const left = nearest(-1);
  const right = nearest(1);
  if (!left || !right) return null;
  const minAll = Math.min(...valid.map((p) => p.poleDistance));
  const lo = Math.min(left.poleDistance, right.poleDistance);
  const hi = Math.max(left.poleDistance, right.poleDistance);
  // A pill sitting closer to the pole than both (e.g. 6 o'clock in the middle
  // of a wedge) owns the pole alone; one side clearly nearer = no contest.
  if (minAll < lo - POLE_TIE_EPSILON_RAD) return null;
  if (!(hi > 0) || lo / hi < POLE_PAIR_MIN_RATIO) return null;
  // They only COMPETE when their natural boxes share a column (one would
  // have to stack on the other); two pills that already sit apart (a sparse
  // wheel) keep their own natural places.
  if (!(left.left < right.right && left.right > right.left)) return null;
  return { left, right };
}

export function resolvePoleStack(
  pills,
  { gap = LABEL_PILL_GAP_PX, minTop = -Infinity, maxBottom = Infinity, axisX } = {}
) {
  const valid = (pills || [])
    .filter(
      (p) =>
        p &&
        Number.isFinite(p.left) &&
        Number.isFinite(p.right) &&
        Number.isFinite(p.top) &&
        Number.isFinite(p.bottom) &&
        Number.isFinite(p.poleDistance) &&
        (p.awayFromPoleSign === 1 || p.awayFromPoleSign === -1)
    )
    .map((p, idx) => ({ ...p, _order: idx }));
  if (!valid.length) return [];

  // Equator first, pole last. On an exact angular tie the EARLIER pill (in
  // clockwise/DOM order) is placed last, so it ends up outermost.
  const pair = Number.isFinite(axisX) ? findPolePair(valid) : null;
  const sorted = [...valid]
    .filter((p) => !pair || (p !== pair.left && p !== pair.right))
    .sort((a, b) => {
      const d = b.poleDistance - a.poleDistance;
      if (Math.abs(d) > POLE_TIE_EPSILON_RAD) return d;
      return b._order - a._order;
    });
  const pairActive = !!pair;
  const minPoleDistance = sorted.length ? sorted[sorted.length - 1].poleDistance : 0;
  const isPole = (p) => p.poleDistance - minPoleDistance <= POLE_TIE_EPSILON_RAD;
  // stgfix-10111425-T419: THE pole pill — the tie-break winner, placed last.
  // With a pole PAIR the pair owns the pole; nobody else is clamped.
  const primaryPole = pairActive ? null : sorted[sorted.length - 1];

  const topHalf = valid[0].awayFromPoleSign === 1;
  const placed = [];
  for (const p of sorted) {
    const height = p.bottom - p.top;
    const naturalCenter = (p.top + p.bottom) / 2;
    let center = naturalCenter;

    if (p === primaryPole) {
      // stgfix-10111425-T419 (MEASURED web-stg main.0636c6a5.js, /wheel/507662,
      // 32 equal segments, 1440x900): 12 o'clock is the BOUNDARY between
      // Segment 1 and the last segment — an exact angular tie. Clamping BOTH
      // tied pills lifted the twin (the last segment) level with, then above,
      // its own neighbour Segment 28 (top 58), while Segment 1 was only
      // clamped against non-pole pills and stayed at 83: the twin sat 25px
      // ABOVE the 12 o'clock pill. Only THE pole pill — the tie-break winner,
      // placed last — is clamped; a tied twin stacks like any other pill. The
      // winner also clears a tied twin it shares a column with (X-overlap); a
      // twin in another column is left alone (see the non-colliding
      // regression test).
      const others = placed.filter(
        (q) => !isPole(q) || (p.left < q.right && p.right > q.left)
      );
      if (others.length) {
        if (topHalf) {
          const highestTop = Math.min(...others.map((q) => q.top));
          center = Math.min(center, highestTop + height / 2);
        } else {
          const lowestBottom = Math.max(...others.map((q) => q.bottom));
          center = Math.max(center, lowestBottom - height / 2);
        }
      }
    }

    // Rules (1)/(2): push outward until clear of every already-placed pill
    // this one genuinely intersects, touching at exactly `gap`. Each push
    // only ever moves further outward, so this terminates; the pass bound is
    // just a belt-and-braces cap.
    for (let pass = 0; pass <= placed.length; pass++) {
      let moved = false;
      for (const q of placed) {
        const top = center - height / 2;
        const bottom = center + height / 2;
        const xOverlaps = p.left < q.right && p.right > q.left;
        const yOverlaps = top < q.bottom + gap && bottom > q.top - gap;
        if (!xOverlaps || !yOverlaps) continue;
        const target = topHalf ? q.top - gap - height / 2 : q.bottom + gap + height / 2;
        if (topHalf ? target < center : target > center) {
          center = target;
          moved = true;
        }
      }
      if (!moved) break;
    }

    placed.push({
      ...p,
      top: center - height / 2,
      bottom: center + height / 2,
      dy: center - naturalCenter,
    });
  }
  if (pairActive) placed.push(...placePolePair(pair, placed, { gap, axisX, topHalf }));
  // Fit the stack into the room: one shared scale factor for the half. A
  // pill whose NATURAL box is already outside the room is left where it is
  // (never moved further out, never pulled in) and doesn't shrink the rest.
  const outsideRoom = (p) =>
    topHalf ? p.top - p.dy < minTop : p.bottom - p.dy > maxBottom;
  let scale = 1;
  for (const p of placed) {
    if (!p.dy || outsideRoom(p)) continue;
    if (topHalf && p.top < minTop) {
      scale = Math.min(scale, (p.top - p.dy - minTop) / -p.dy);
    } else if (!topHalf && p.bottom > maxBottom) {
      scale = Math.min(scale, (maxBottom - (p.bottom - p.dy)) / p.dy);
    }
  }
  let fitted = placed.map((p) => {
    if (!p.dy) return p;
    const dy = outsideRoom(p) ? 0 : p.dy * scale;
    if (dy === p.dy) return p;
    return { ...p, top: p.top - p.dy + dy, bottom: p.bottom - p.dy + dy, dy };
  });
  if (pairActive) {
    // The shared scale can split the pair (each has its own natural Y): put
    // both back on ONE line — the more outward of the two, kept in the room.
    const twins = fitted.filter((p) => p._polePair);
    const centers = twins.map((p) => (p.top + p.bottom) / 2);
    let line = topHalf ? Math.min(...centers) : Math.max(...centers);
    const h = Math.max(...twins.map((p) => p.bottom - p.top));
    if (topHalf && Number.isFinite(minTop)) line = Math.max(line, minTop + h / 2);
    if (!topHalf && Number.isFinite(maxBottom)) line = Math.min(line, maxBottom - h / 2);
    fitted = fitted.map((p) => {
      if (!p._polePair) return p;
      const delta = line - (p.top + p.bottom) / 2;
      return { ...p, top: p.top + delta, bottom: p.bottom + delta, dy: p.dy + delta };
    });
  }

  return fitted
    .sort((a, b) => a._order - b._order)
    .map(({ _order, _polePair, ...rest }) => rest);
}

// The pole pair: centres mirrored about axisX (equal |centre - axis|), as
// close as the wider pill allows (touching at `gap` when the widths match),
// ONE shared centre line,
// pushed outward clear of every pill already placed, and — the pole rule —
// at least as far out as the half's outermost other pill.
function placePolePair(pair, placed, { gap, axisX, topHalf }) {
  const twins = [pair.left, pair.right];
  const cx = (p) => (p.left + p.right) / 2;
  const half = Math.max(...twins.map((p) => p.right - p.left)) / 2 + gap / 2;
  const off = half;
  const height = Math.max(...twins.map((p) => p.bottom - p.top));
  const boxes = twins.map((p, i) => {
    const w = p.right - p.left;
    const c = axisX + (i === 0 ? -off : off);
    return { p, left: c - w / 2, right: c + w / 2, dx: c - cx(p), h: p.bottom - p.top };
  });
  const naturalCenters = twins.map((p) => (p.top + p.bottom) / 2);
  let center = topHalf ? Math.min(...naturalCenters) : Math.max(...naturalCenters);
  if (placed.length) {
    center = topHalf
      ? Math.min(center, Math.min(...placed.map((q) => q.top)) + height / 2)
      : Math.max(center, Math.max(...placed.map((q) => q.bottom)) - height / 2);
  }
  for (let pass = 0; pass <= placed.length * 2; pass++) {
    let moved = false;
    for (const b of boxes) {
      for (const q of placed) {
        const top = center - height / 2;
        const bottom = center + height / 2;
        if (!(b.left < q.right && b.right > q.left)) continue;
        if (!(top < q.bottom + gap && bottom > q.top - gap)) continue;
        const target = topHalf ? q.top - gap - height / 2 : q.bottom + gap + height / 2;
        if (topHalf ? target < center : target > center) {
          center = target;
          moved = true;
        }
      }
    }
    if (!moved) break;
  }
  return boxes.map(({ p, left, right, dx, h }) => ({
    ...p,
    left,
    right,
    top: center - h / 2,
    bottom: center + h / 2,
    dy: center - (p.top + p.bottom) / 2,
    dx,
    _polePair: true,
  }));
}

// Normalizes an angle (radians) to (-PI, PI].
function normalizeAngleRad(angle) {
  let a = angle % (2 * Math.PI);
  if (a > Math.PI) a -= 2 * Math.PI;
  if (a <= -Math.PI) a += 2 * Math.PI;
  return a;
}

// Classifies a full set of REAL, already-rendered pill rects into the 12
// o'clock half (pole = 12 o'clock / angle 0) and the 6 o'clock half (pole =
// 6 o'clock / angle +-PI) purely by each pill's OWN `midAngle` (radians, 0 =
// 12 o'clock, clockwise positive — the same convention pillSafeAnchorRadius
// already uses), THEN resolves each half independently with resolvePoleStack.
// Classifying by angle (not by rendered Y) is what makes rule (3) immune to
// the text-height skew described above. Pure; returns a flat array (order
// not significant — callers key off `id`) where every entry carries `dy`,
// the vertical page-pixel delta to apply.
export function resolveTightPillStack(
  pills,
  { gap = LABEL_PILL_GAP_PX, minTop = -Infinity, maxBottom = Infinity, axisX } = {}
) {
  const top = [];
  const bottom = [];
  for (const p of pills || []) {
    if (!p || !Number.isFinite(p.top) || !Number.isFinite(p.bottom) || !Number.isFinite(p.midAngle)) {
      continue;
    }
    const normalized = normalizeAngleRad(p.midAngle);
    const distFromNoon = Math.abs(normalized);
    const isTop = distFromNoon <= Math.PI / 2;
    if (isTop) {
      top.push({ ...p, poleDistance: distFromNoon, awayFromPoleSign: 1 });
    } else {
      bottom.push({ ...p, poleDistance: Math.PI - distFromNoon, awayFromPoleSign: -1 });
    }
  }
  return [
    ...resolvePoleStack(top, { gap, minTop, maxBottom, axisX }),
    ...resolvePoleStack(bottom, { gap, minTop, maxBottom, axisX }),
  ];
}

// audit-10101940-box-editwheel-1 (T4.18 residual): applyTightPillStack's
// vertical stack can be UNDONE by the ring-safety pass that follows it — near
// 12/6 o'clock "away from the pole" is "toward the ring", so ring-safety
// pushes the pill back out along its own angle and re-creates an overlap
// (MEASURED web-stg 507379 @1440: "Total Body Water Percentage" vs
// "Extracellular Water Volume" overlapped 11x19px AFTER the stack). When the
// leftover overlap is a narrow X sliver, shift the pill that sits FARTHER from
// the wheel's vertical axis further AWAY from that axis by exactly the X
// overlap (0px gap touch). Moving away from the axis never moves a pill toward
// the ring. Refuses a shift that would leave [minX, maxX] (the clip boundary)
// or that exceeds maxShiftFraction (0.75; was 0.5, which left the 32-segment
// wheel's 62%-overlap pairs untouched, audit-10111315-box-editwheel-6) of the
// pill's width (a genuinely stacked column is the vertical resolver's job).
// Each pass makes ONE move, so maxPasses is a move budget: 8 ran out on the
// 32-segment wheel (needs 7+ cumulative moves; audit-10111612-box-editwheel-1,
// web-stg 507662 left 3 pairs overlapping after #6857). Pure; returns [{id, dx}] for pills
// that must move (dx in page px, cumulative over passes).
export function resolveResidualPillOverlaps(
  pills,
  {
    axisX,
    minX = -Infinity,
    maxX = Infinity,
    maxShiftFraction = 0.75,
    maxPasses = 400,
    obstacles = [],
  } = {}
) {
  const eps = 0.5;
  const rects = (pills || [])
    .filter((p) => p && [p.left, p.right, p.top, p.bottom].every(Number.isFinite) && Number.isFinite(axisX))
    .map((p) => ({ id: p.id, left: p.left, right: p.right, top: p.top, bottom: p.bottom, dx: 0 }));
  const sideOf = (p) => ((p.left + p.right) / 2 >= axisX ? 1 : -1);
  const distOf = (p) => Math.abs((p.left + p.right) / 2 - axisX);
  const canMove = (p, ox) => {
    const dir = sideOf(p);
    const shift = ox * dir;
    const w = p.right - p.left;
    if (ox > w * maxShiftFraction) return null;
    if (p.left + shift < minX || p.right + shift > maxX) return null;
    // stgfix-10130814-T22131: never shift a pill under a control painted over
    // the canvas (the Builder's Undo/Redo cluster) it was not already under.
    const hits = (l, r) =>
      (obstacles || []).some(
        (o) => o && l < o.right && r > o.left && p.top < o.bottom && p.bottom > o.top
      );
    if (hits(p.left + shift, p.right + shift) && !hits(p.left, p.right)) return null;
    return shift;
  };
  for (let pass = 0; pass < maxPasses; pass++) {
    let moved = false;
    for (let i = 0; i < rects.length && !moved; i++) {
      for (let j = i + 1; j < rects.length && !moved; j++) {
        const a = rects[i];
        const b = rects[j];
        const ox = Math.min(a.right, b.right) - Math.max(a.left, b.left);
        const oy = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top);
        if (!(ox > eps && oy > eps)) continue;
        // Prefer the pill farther from the axis (ties: the later pill).
        const order = distOf(a) > distOf(b) ? [a, b] : [b, a];
        for (const p of order) {
          // Only meaningful when the two pills sit on the SAME side — moving
          // away from the axis must separate them.
          const shift = canMove(p, ox);
          if (shift == null) continue;
          p.left += shift;
          p.right += shift;
          p.dx += shift;
          moved = true;
          break;
        }
      }
    }
    if (!moved) break;
  }
  return rects.filter((p) => p.dx).map((p) => ({ id: p.id, dx: p.dx }));
}
